<template>
  <lf-card :has-header-divider="false" class="mb-8 py-6">
    <template #header>
      <div class="flex">
        <lf-h3>
          {{ $t("DEALS.DEAL_PROGRESS.AFFILIATED_BUSINESSES_INFO.TITLE", 2) }}
        </lf-h3>
        <div
          class="mx-2 mt-0-5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-100"
        >
          <span
            class="-mt-0-25 font-medium text-blue-500"
            data-cy="businesses-count"
          >
            {{ businessCount }}
          </span>
        </div>
      </div>

      <more-button v-if="canEditBusinessInfo">
        <ul>
          <li
            v-if="selectedAffiliatedBusiness"
            @click.stop="
              selectAffiliatedBusinessToEdit(selectedAffiliatedBusiness)
            "
          >
            {{ $t("COMMON.EDIT") }}
            {{ selectedAffiliatedBusiness.affiliated_businesses_legal_name }}
          </li>
          <li @click.stop="selectAffiliatedBusinessToEdit(undefined)">
            {{ $t("DEALS.DEAL_PROGRESS.AFFILIATED_BUSINESSES_INFO.ADD_NEW") }}
          </li>
          <li
            v-if="canDeleteAffiliatedBusiness && selectedAffiliatedBusiness"
            class="text-red-500"
            @click="showDeleteModal = true"
          >
            {{ $t("COMMON.DELETE") }}
          </li>
        </ul>
      </more-button>
    </template>
    <template #default>
      <div
        v-if="affiliatedBusinesses.length"
        class="-mt-10 flex flex-wrap items-center gap-4"
      >
        <lf-checkmark-tab
          v-for="(affiliatedBusiness, index) in affiliatedBusinesses"
          :key="affiliatedBusiness.affiliated_business_id"
          :active="
            isSelectedAffiliatedBusiness(
              affiliatedBusiness.affiliated_business_id
            )
          "
          class="cursor-pointer"
          @click="
            selectAffiliatedBusiness(affiliatedBusiness.affiliated_business_id)
          "
        >
          {{
            affiliatedBusiness.affiliated_businesses_legal_name
              ? affiliatedBusiness.affiliated_businesses_legal_name
              : $t("COMMON.BUSINESS") + " " + (index + 1)
          }}
        </lf-checkmark-tab>
      </div>
      <hr class="my-6" />
      <div
        v-if="affiliatedBusinesses.length"
        class="mt-3 grid grid-cols-3 gap-5"
      >
        <div v-for="field in FIELDS_DETAILS" :key="field.id">
          <workflow-field
            :id="field.id"
            :label="field.title"
            :value="field.value"
            click-to-copy
            is-business
          />
        </div>
      </div>
      <div v-else>
        {{ $t("DEALS.DEAL_PROGRESS.AFFILIATED_BUSINESSES_INFO.NO_INFO") }}
      </div>
    </template>
  </lf-card>
  <affiliated-business-info-modal
    v-if="isModalOpen"
    :close="handleClose"
    :affiliated-business-info="affiliatedBusinessToEdit"
    @update-data="updateSelectedBusiness"
  />
  <confirm-modal
    v-if="showDeleteModal"
    :title="$t('DEALS.DEAL_PROGRESS.AFFILIATED_BUSINESSES_INFO.DELETE')"
    :description="
      $t('DEALS.DEAL_PROGRESS.AFFILIATED_BUSINESSES_INFO.DELETE_BODY')
    "
    :confirm-button="$t('COMMON.DELETE')"
    confirm-button-type="error"
    :close="() => (showDeleteModal = false)"
    @confirm="deleteAffiliatedBusiness"
  />
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { format } from "date-fns";
import isEmpty from "lodash/isEmpty";

import {
  AFFILITED_BUSINESS_INFO_FIELD_IDS,
  WORKFLOW_BLOCKS_IDS,
} from "@/helpers/constants/workflow";
import { formatAddress } from "@/helpers/formatting";
import { formatMoney } from "@/helpers/formatting";
import { useAuth, useDataCollectionCanEdit } from "@/hooks/auth";
import { useDeals } from "@/hooks/deals";
import { useWorkflowFields } from "@/hooks/workflow";

import MoreButton from "./MoreButton.vue";
import WorkflowField from "@/components/deals/WorkflowLive/WorkflowLiveField.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import LfCheckmarkTab from "@/components/ui/LfCheckmarkTab.vue";
import AffiliatedBusinessInfoModal from "@/views/deals/modals/AffiliatedBusinessInfoModal.vue";

import type { IAddress } from "@/models/common";
import type { IRootState } from "@/models/state";
import type {
  AffiliatedBusinessInfoFields,
  PhoneNumber,
} from "@/models/workflows";

type AffiliatedBusinessFields = AffiliatedBusinessInfoFields | undefined | null;

const { getters, commit, dispatch } = useStore<IRootState>();
const { isActiveDealCanadian, canEditDealProgress } = useDeals();
const { isFunder, isClient } = useAuth();
const { t } = useI18n();
const canDeleteAffiliatedBusiness = useDataCollectionCanEdit();
const { isFieldVisible, fieldData: affiliatedBusinesses } = useWorkflowFields(
  WORKFLOW_BLOCKS_IDS.affiliated_businesses
);

const showDeleteModal = ref(false);
const isModalOpen = ref(false);
const affiliatedBusinessToEdit = ref<AffiliatedBusinessFields>(undefined);
const selectedId = ref<
  AffiliatedBusinessInfoFields["affiliated_business_id"] | null
>(affiliatedBusinesses.value?.[0]?.affiliated_business_id || null);

commit("applications/setSelectedAffiliatedBusinessId", selectedId.value);

const existingData = ref<
  AffiliatedBusinessInfoFields["affiliated_business_id"][]
>([]);

const selectedAffiliatedBusiness =
  computed<AffiliatedBusinessInfoFields | null>(
    () =>
      affiliatedBusinesses.value?.find(
        ({ affiliated_business_id }) =>
          affiliated_business_id === selectedId.value
      ) || null
  );

const businessCount = computed(() => affiliatedBusinesses.value?.length || 0);

const canEditBusinessInfo = computed(
  () => (!isFunder || isClient) && canEditDealProgress.value
);

const businessEntityType = computed(() => {
  const entityType =
    selectedAffiliatedBusiness.value?.affiliated_businesses_entity_type;
  if (entityType) {
    return getters["options/businessEntityTypes"](entityType);
  }
  return "-";
});

const startDate = computed(() =>
  selectedAffiliatedBusiness.value?.affiliated_businesses_start_date
    ? format(
        new Date(
          selectedAffiliatedBusiness.value
            ?.affiliated_businesses_start_date as string
        ),
        "MM/dd/yyyy"
      )
    : ""
);
const addresses = computed(() => {
  if (
    selectedAffiliatedBusiness.value?.affiliated_businesses_addresses?.length
  ) {
    const allAdresses = selectedAffiliatedBusiness.value
      ?.affiliated_businesses_addresses as IAddress[];

    return allAdresses.map((address, index) => ({
      id: AFFILITED_BUSINESS_INFO_FIELD_IDS.address,
      title:
        index === 0
          ? t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.PRIMARY_ADDRESS")
          : `${t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.ADDRESS")} ${index + 1}`,
      value: formatAddress(address),
    }));
  } else {
    return [
      {
        id: AFFILITED_BUSINESS_INFO_FIELD_IDS.address,
        title: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.PRIMARY_ADDRESS"),
        value: "-",
      },
    ];
  }
});

const phoneData = computed(() => {
  if (
    !isEmpty(
      selectedAffiliatedBusiness.value?.affiliated_businesses_phone_numbers
    )
  ) {
    const allPhones = selectedAffiliatedBusiness.value
      ?.affiliated_businesses_phone_numbers as PhoneNumber[];
    return allPhones.map((phone, index) => ({
      id: AFFILITED_BUSINESS_INFO_FIELD_IDS.phone_numbers,
      title: phone.is_primary
        ? t("DEALS.DEAL_PROGRESS.PERSONAL_INFO.PRIMARY_PHONE")
        : `${t("DEALS.DEAL_PROGRESS.PERSONAL_INFO.PHONE")} ${index + 1}`,
      value: phone.phone_number,
    }));
  } else {
    return [
      {
        id: AFFILITED_BUSINESS_INFO_FIELD_IDS.phone_numbers,
        title: t("DEALS.DEAL_PROGRESS.PERSONAL_INFO.PRIMARY_PHONE"),
        value: "-",
      },
    ];
  }
});

const statedMonthlyRevenue = computed((): string => {
  const revenue =
    selectedAffiliatedBusiness.value?.[
      AFFILITED_BUSINESS_INFO_FIELD_IDS.stated_monthly_revenue
    ];
  return revenue ? formatMoney(revenue) : "-";
});

const FIELDS_DETAILS = computed(() =>
  [
    {
      id: AFFILITED_BUSINESS_INFO_FIELD_IDS.legal_name,
      title: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.LEGAL_NAME"),
      value: selectedAffiliatedBusiness.value?.affiliated_businesses_legal_name,
    },
    {
      id: AFFILITED_BUSINESS_INFO_FIELD_IDS.dba,
      title: t("DEALS.DEAL_PROGRESS.PERSONAL_INFO.DBA"),
      value: selectedAffiliatedBusiness.value?.affiliated_businesses_dba,
    },
    {
      id: AFFILITED_BUSINESS_INFO_FIELD_IDS.stated_monthly_revenue,
      title: t("DEALS.TABLE_HEADERS.STATED_MONTHLY_REVENUE"),
      value: statedMonthlyRevenue.value,
    },
    {
      id: AFFILITED_BUSINESS_INFO_FIELD_IDS.entity_type,
      title: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.STRUCTURE_TYPE"),
      value: businessEntityType.value,
    },
    {
      id: AFFILITED_BUSINESS_INFO_FIELD_IDS.start_date,
      title: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.START_DATE"),
      value: startDate.value,
    },
    {
      id: AFFILITED_BUSINESS_INFO_FIELD_IDS.state_of_incorporation,
      title: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.STATE_OF_INCORPORATION"),
      value:
        selectedAffiliatedBusiness.value
          ?.affiliated_businesses_state_of_incorporation,
    },
    {
      id: AFFILITED_BUSINESS_INFO_FIELD_IDS.ein,
      title: isActiveDealCanadian.value
        ? t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.BN")
        : t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.EIN"),
      value: selectedAffiliatedBusiness.value?.affiliated_businesses_ein,
    },
    ...addresses.value,
    ...phoneData.value,
  ].filter((field) => field?.id && isFieldVisible(field.id))
);

const selectAffiliatedBusinessToEdit = (
  affiliatedBusiness: AffiliatedBusinessFields
) => {
  affiliatedBusinessToEdit.value = affiliatedBusiness;
  existingData.value =
    affiliatedBusinesses.value?.flatMap(
      ({ affiliated_business_id }) => affiliated_business_id
    ) || [];
  isModalOpen.value = true;
};

const isSelectedAffiliatedBusiness = (
  id: AffiliatedBusinessInfoFields["affiliated_business_id"]
) => selectedId.value === id;

const selectAffiliatedBusiness = (
  id: AffiliatedBusinessInfoFields["affiliated_business_id"]
) => {
  selectedId.value = id;
  commit("applications/setSelectedAffiliatedBusinessId", id);
};

const updateSelectedBusiness = () => {
  const business = affiliatedBusinesses.value.find(
    ({ affiliated_business_id }) =>
      !existingData.value.includes(affiliated_business_id)
  );

  if (business) {
    selectedId.value = business.affiliated_business_id;
    commit("applications/setSelectedAffiliatedBusinessId", selectedId.value);
  }
};

const deleteAffiliatedBusiness = async () => {
  if (!selectedId.value) {
    return;
  }

  const payload = {
    affiliated_businesses: [
      {
        affiliated_business_id: selectedId.value,
        _delete: true,
      },
    ],
  };

  try {
    await dispatch("workflows/updateWorkflowSnapshotFields", payload);
    selectedId.value =
      affiliatedBusinesses.value?.[0]?.affiliated_business_id || null;
    commit("applications/setSelectedAffiliatedBusinessId", selectedId.value);
  } catch (error) {
    return;
  }
};
const handleClose = () => (isModalOpen.value = false);
</script>
